/**
 * EAA-1826: which modifier key this machine actually has.
 *
 * The console renders its global-search hint as a literal `⌘K`. On Windows and
 * Linux — where the chord is Ctrl+K and there is no ⌘ key on the keyboard —
 * that hint names a key the user cannot press. The HANDLER was always correct
 * (`useCommandPalette` accepts `metaKey || ctrlKey`); only the LABEL was wrong,
 * which is the worst version of the bug: the shortcut works, so nothing looks
 * broken, and the user simply never tries it.
 *
 * Lives in `packages/ui` rather than in the portal because every app that
 * renders a shortcut hint needs the same answer, and because a second copy is
 * how the two drift.
 */

/** `⌘` on Apple platforms, `Ctrl` everywhere else. */
export type ModifierKeyLabel = '⌘' | 'Ctrl';

/**
 * True on macOS/iPadOS/iOS.
 *
 * `navigator.platform` is deprecated but is still the only thing every current
 * browser agrees on; `userAgentData.platform` is Chromium-only, so it is tried
 * first and `platform` is the fallback rather than the reverse. iPadOS reports
 * "MacIntel", which is correct for this purpose — an iPad with a keyboard has a
 * ⌘ key.
 *
 * Returns `false` when there is no `navigator` at all (SSR, tests), because
 * `Ctrl` is the safer default: it is right for the large majority of users, and
 * a Mac user shown "Ctrl" still has a working shortcut, whereas the reverse
 * names a key that does not exist on the keyboard in front of them.
 */
export function isApplePlatform(): boolean {
    if (typeof navigator === 'undefined') return false;

    const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } })
        .userAgentData;
    const platform = uaData?.platform || navigator.platform || '';
    if (platform) return /mac|iphone|ipad|ipod/i.test(platform);

    return /mac|iphone|ipad|ipod/i.test(navigator.userAgent || '');
}

/** The modifier label to render in a shortcut hint on this machine. */
export function modifierKeyLabel(): ModifierKeyLabel {
    return isApplePlatform() ? '⌘' : 'Ctrl';
}

/**
 * Render a chord for this platform. `mod` resolves to ⌘ or Ctrl.
 *
 * SEPARATOR AND ORDER BOTH FOLLOW THE PLATFORM, and neither is cosmetic:
 *
 * - Apple omits the separator and puts ⌘ CLOSEST TO THE KEY, modifiers running
 *   ⌃⌥⇧⌘ outwards — so `⇧⌘K`, never `⌘⇧K` or `⌘+K`.
 * - Windows/Linux keep `+` and lead with Ctrl — so `Ctrl+Shift+K`, never
 *   `Shift+Ctrl+K`.
 *
 * Emitting one platform's order on the other is the kind of detail that reads as
 * a typo to anyone who uses that OS, so the two orders are built separately
 * rather than from one shared list.
 *
 *   formatShortcut('K')                   // '⌘K'   | 'Ctrl+K'
 *   formatShortcut('K', 'shift')          // '⇧⌘K'  | 'Ctrl+Shift+K'
 *   formatShortcut('M', 'alt', 'shift')   // '⌥⇧⌘M' | 'Ctrl+Alt+Shift+M'
 */
export function formatShortcut(key: string, ...extra: Array<'shift' | 'alt'>): string {
    const alt = extra.includes('alt');
    const shift = extra.includes('shift');

    if (isApplePlatform()) {
        // ⌘ sits adjacent to the key; the others stack outwards.
        return `${alt ? '⌥' : ''}${shift ? '⇧' : ''}⌘${key}`;
    }
    const parts = ['Ctrl'];
    if (alt) parts.push('Alt');
    if (shift) parts.push('Shift');
    parts.push(key);
    return parts.join('+');
}
